import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, PlayCircle } from 'lucide-react';

/**
 * Tutorials page — shows free courses (price_cents === 0).
 * Same data model as Courses; framed differently for visitors who want
 * a no-commitment way to sample what Learnable offers.
 */
export default function Tutorials() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/courses')
      .then((r) => r.json())
      .then((data) => setCourses((data.courses || []).filter((c) => c.price_cents === 0)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <section className="bg-gradient-to-b from-brand/5 to-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">
            Free for everyone
          </p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Tutorials
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Free walk-throughs covering software, compliance, and productivity topics.
            No payment required — just create a free account and start watching.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        {loading && <p className="text-slate-500">Loading…</p>}
        {!loading && courses.length === 0 && (
          <div className="text-center py-12">
            <PlayCircle className="text-slate-300 mx-auto mb-3" size={48} />
            <p className="text-slate-500">New tutorials coming soon. Check back shortly.</p>
            <Link to="/" className="inline-block mt-4 text-brand font-medium hover:underline">
              ← Back to home
            </Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => (
            <Link
              key={c.id}
              to={`/courses/${c.slug}`}
              className="block border border-slate-200 rounded-lg bg-white overflow-hidden hover:border-brand hover:shadow-md transition group"
            >
              {c.hero_image_url ? (
                <img src={c.hero_image_url} alt="" className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center">
                  <PlayCircle className="text-emerald-600/50" size={44} />
                </div>
              )}
              <div className="p-4">
                <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded mb-2">
                  Free
                </span>
                <h3 className="font-semibold text-lg text-slate-900 group-hover:text-brand transition-colors">
                  {c.title}
                </h3>
                <p className="text-sm text-slate-600 line-clamp-2 mt-1">{c.short_description}</p>
                {c.duration_minutes && (
                  <div className="text-xs text-slate-500 mt-3">{c.duration_minutes} min</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
